import { Plus, X } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

interface Props {
  nomeDoAnalito: string;
  apelidos: string[];
  somenteLeitura: boolean;
  aoMudar: (apelidos: string[]) => void;
}

/**
 * Os outros nomes com que o laboratório manda ESTE analito.
 *
 * Fica visível na grade, e não escondido num diálogo, porque é a informação que
 * explica um laudo chegando sem uma linha: o Vertis mandou `BASAL`, o catálogo
 * esperava `RESULTADO BASAL`, e o resultado não casou. Ver o backend em
 * `modules/sync/lib/casamento.ts`.
 */
export function ApelidosDoAnalito({ nomeDoAnalito, apelidos, somenteLeitura, aoMudar }: Props) {
  const [novo, setNovo] = useState('');
  const [aberto, setAberto] = useState(false);

  function acrescentar() {
    const limpo = novo.trim();
    if (!limpo) return;

    // Comparação sem caixa: `basal` e `BASAL` são o mesmo apelido, e o
    // casamento no servidor também ignora caixa.
    const repetido = apelidos.some((a) => a.toLocaleLowerCase() === limpo.toLocaleLowerCase());
    const eOProprioNome = limpo.toLocaleLowerCase() === nomeDoAnalito.trim().toLocaleLowerCase();

    if (!repetido && !eOProprioNome && apelidos.length < 10) aoMudar([...apelidos, limpo]);
    setNovo('');
    setAberto(false);
  }

  if (somenteLeitura && apelidos.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-1">
      <span className="text-xs text-muted-foreground">Apelidos do Vertis:</span>

      {apelidos.length === 0 && !aberto && (
        <span className="text-xs text-muted-foreground/70">nenhum</span>
      )}

      {apelidos.map((apelido) => (
        <Badge key={apelido} variant="outline" className="gap-1 font-normal">
          {apelido}
          {!somenteLeitura && (
            <button
              type="button"
              aria-label={`Remover apelido ${apelido}`}
              className="hover:text-destructive"
              onClick={() => aoMudar(apelidos.filter((a) => a !== apelido))}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </Badge>
      ))}

      {!somenteLeitura &&
        (aberto ? (
          <Input
            autoFocus
            aria-label={`Novo apelido para ${nomeDoAnalito}`}
            className="h-6 w-40 text-xs"
            value={novo}
            onChange={(e) => setNovo(e.target.value)}
            onBlur={acrescentar}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                acrescentar();
              }
              if (e.key === 'Escape') {
                setNovo('');
                setAberto(false);
              }
            }}
          />
        ) : (
          apelidos.length < 10 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-1.5"
              aria-label={`Adicionar apelido em ${nomeDoAnalito}`}
              onClick={() => setAberto(true)}
            >
              <Plus className="w-3 h-3" />
            </Button>
          )
        ))}
    </div>
  );
}
